import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { estApiError } from '../api/errors.js';
import { annoncerConfinement, estRefusConfinement, marquerConfine } from './confinement.js';
import { identiteCourante, oublierIdentite, PREFIXE_METIER, surCompteChange } from './identite.js';

/** Clé de la requête de session, partagée entre le chargeur et `useSession`. */
export const CLE_SESSION = ['session'] as const;

/** Une seule relecture en vol, y compris si la session elle-même refuse. */
let relectureEnCours = false;

function surErreur(erreur: unknown): void {
  // Confinement (CGU, enrôlement à la double authentification) : la coquille
  // revalide sa route, et la session relue mène à la page qui la libère.
  if (estRefusConfinement(erreur)) {
    const identite = identiteCourante();
    if (identite) {
      marquerConfine(identite.compteId);
      annoncerConfinement();
    }
    return;
  }
  if (!estApiError(erreur) || !['module.indisponible', 'acces.refuse'].includes(erreur.code))
    return;
  if (relectureEnCours) return;
  const identite = identiteCourante();
  if (!identite) return;
  relectureEnCours = true;
  // La coquille observe la session avec enabled:false : invalidateQueries
  // ne la relit pas. Le chargeur relit ET purge les données des anciens droits.
  void import('./session.js')
    .then(async ({ chargerSession }) => {
      if (identiteCourante() !== identite) return;
      await chargerSession(identite.galerieSlug);
    })
    .catch(() => undefined)
    .finally(() => {
      relectureEnCours = false;
    });
}

/**
 * Instance unique, partagée entre `main.tsx` (`QueryClientProvider`) et les
 * chargeurs de route (mode data de react-router 7) : un chargeur qui appelle
 * `queryClient.ensureQueryData` réutilise le cache posé par les composants,
 * et réciproquement.
 */
export const queryClient: QueryClient = new QueryClient({
  queryCache: new QueryCache({ onError: surErreur }),
  mutationCache: new MutationCache({ onError: surErreur }),
  defaultOptions: {
    queries: {
      retry: false,
      staleTime: 60_000,
    },
  },
});

/**
 * `409 compte_change` (constat N3) : le serveur ne sert plus le compte
 * capturé. Si c'est l'identité COURANTE, elle est oubliée — requêtes en vol
 * annulées, cache entier vidé, nouvelle époque — et la coquille revalide sa
 * route pour adopter la session réelle (`RegieShell`) ; sans cela, un
 * composant monté redemanderait sans fin sous l'ancien compte. Sinon, seules
 * les requêtes restantes de ce compte sont retirées.
 */
surCompteChange((compteId) => {
  if (identiteCourante()?.compteId === compteId) {
    void oublierIdentite(queryClient);
    return;
  }
  void queryClient.cancelQueries({ queryKey: [PREFIXE_METIER, compteId] });
  queryClient.removeQueries({ queryKey: [PREFIXE_METIER, compteId] });
});
